/**
 * Create Episode Dropdown Button
 *
 * Split button for creating episodes with two behaviors:
 * - Main button: Executes current method (navigates immediately)
 * - Dropdown: Changes preferred method (no immediate navigation)
 *
 * This prevents accidental navigation when selecting dropdown options.
 *
 * Story: 11.8 - LLM-Assisted Episode Editing
 * Acceptance Criteria: AC 2 - Task 3
 */

import { useState, useEffect, useRef } from 'react';
import { type EpisodeCreationMethod } from '../types/episode.types';

// ============================================================================
// Types
// ============================================================================

interface CreateEpisodeButtonProps {
  /** Called when main button is clicked (executes action) */
  onExecute: (method: EpisodeCreationMethod) => void;
  /** Called when dropdown option is selected (changes preference only) */
  onMethodChange: (method: EpisodeCreationMethod) => void;
  /** Currently selected method */
  currentMethod: EpisodeCreationMethod;
  /** Whether import is in progress */
  isImporting?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function CreateEpisodeButton({
  onExecute,
  onMethodChange,
  currentMethod,
  isImporting = false,
}: CreateEpisodeButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const handleOptionClick = (method: EpisodeCreationMethod, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsOpen(false);
    onMethodChange(method);
  };

  const handleMainButtonClick = () => {
    // Execute the current method (navigate)
    onExecute(currentMethod);
  };

  const getButtonText = () => {
    if (isImporting) return 'Importing...';
    if (currentMethod === 'generate') return 'Generate With AI';
    if (currentMethod === 'import') return 'Import from JSON';
    return 'Create Episode';
  };

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <div className="flex">
        {/* Main Button */}
        <button
          type="button"
          onClick={handleMainButtonClick}
          disabled={isImporting}
          className="px-4 py-2 bg-blue-600 text-white rounded-l-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="create-episode-button-main"
        >
          {getButtonText()}
        </button>

        {/* Dropdown Arrow Button */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="px-3 py-2 bg-blue-600 text-white border-l border-blue-500 rounded-r-md hover:bg-blue-700 transition-colors"
          data-testid="create-episode-button-dropdown"
          aria-label="Toggle dropdown"
        >
          <svg
            className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-10"
          data-testid="create-episode-dropdown-menu"
        >
          <div className="py-1">
            <button
              type="button"
              onClick={(e) => handleOptionClick('manual', e)}
              className={`w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors flex items-center gap-3 ${
                currentMethod === 'manual' ? 'bg-blue-50' : ''
              }`}
              data-testid="create-episode-option-manual"
            >
              <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
              <div className="flex-1">
                <div className="font-medium text-gray-900">Manual Creation</div>
                <div className="text-sm text-gray-500">Create episode using form</div>
              </div>
            </button>

            <button
              type="button"
              onClick={(e) => handleOptionClick('generate', e)}
              className={`w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors flex items-center gap-3 ${
                currentMethod === 'generate' ? 'bg-blue-50' : ''
              }`}
              data-testid="create-episode-option-generate"
            >
              <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13 7H7v6h6V7z" />
                <path
                  fillRule="evenodd"
                  d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="flex-1">
                <div className="font-medium text-gray-900">Generate with AI</div>
                <div className="text-sm text-gray-500">Create episode using AI</div>
              </div>
            </button>

            <div className="border-t border-gray-100" />

            <button
              type="button"
              onClick={(e) => handleOptionClick('import', e)}
              disabled={isImporting}
              className={`w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed ${
                currentMethod === 'import' ? 'bg-blue-50' : ''
              }`}
              data-testid="create-episode-option-import"
            >
              <svg
                className="w-5 h-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                />
              </svg>
              <div className="flex-1">
                <div className="font-medium text-gray-900">Import from JSON</div>
                <div className="text-sm text-gray-500">Import episode from exported file</div>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
